






























































import { computed, defineComponent, onUnmounted, PropType, ref, toRef, watch } from "@vue/composition-api";
import { debounce, isEqual } from "lodash-es";
import { CollabLiveChat } from "@scrile/api-provider/dist/api/CollabLiveChatsProvider";
import {
  LiveChatInvitation,
  LiveChatInvitationStatus,
  LiveChatInvitationSubjectType,
} from "@scrile/api-provider/dist/api/LiveChatInvitationsProvider";
import { LivechatSubjectType } from "@scrile/api-provider/dist/api/LivechatsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { EVENTS, MAX_COLLAB_INVITES } from "@/const";
import AvatarDescription from "@/components/AvatarDescription";
import BaseButton from "@/components/BaseButton";
import BaseCheckbox from "@/components/BaseCheckbox";
import BaseSpinner from "@/components/BaseSpinner";
import CustomScroll from "@/components/CustomScroll";
import ListItem from "@/components/ListItem";
import PageTitle from "@/components/PageTitle/PageTitle";
import BroadcastingCreatorItem from "./components/BroadcastingCreatorItem.vue";
import InvitationItem from "./components/InvitationItem.vue";
import useLiveChatInvitation from "../../../hooks/useLiveChatInvitation";
import InviteCreatorsModal from "../InviteCreatorsModal";

export default defineComponent({
  name: "CollabLiveSettings",
  components: {
    BroadcastingCreatorItem,
    BaseCheckbox,
    CustomScroll,
    BaseSpinner,
    InvitationItem,
    InviteCreatorsModal,
    AvatarDescription,
    ListItem,
    BaseButton,
    PageTitle,
  },
  props: {
    collab: { type: Object as PropType<CollabLiveChat>, required: true },
    usersAmountByReferrer: { type: Object as PropType<Record<string, number>>, required: true },
  },
  setup(props) {
    const { authUserStrict } = useAuthUser();
    const { invitations, invitationsCreate, cancelInvitation, invitationsSearch } = useLiveChatInvitation();

    const loading = ref(false);
    const showInviteModal = ref(false);
    const moderatorIds = ref<string[]>(props.collab.moderatorIds);
    const afterUpdateCbs = ref<(() => void)[]>([]);
    const processing = ref(false);

    const broadcastingUsersNumber = computed(() => props.collab.broadcasterUserIds.length);
    const pendingInvitation = computed(() => invitations.value.filter(i => i.status === LiveChatInvitationStatus.NEW));
    const acceptedInvitation = computed(() =>
      invitations.value.filter(i => i.status === LiveChatInvitationStatus.ACCEPTED)
    );
    const userIdsToExcludeInSearch = computed(() => invitations.value.map(i => i.recipientId));
    const showInviteButton = computed(() => invitations.value.length < MAX_COLLAB_INVITES);

    const isUserModerator = (invitation: LiveChatInvitation) =>
      props.collab.moderatorIds.includes(invitation.recipientId);

    const getUserAmountByReferrer = (id: string) => {
      return props.usersAmountByReferrer[id] || 0;
    };

    const onCreateInvitations = async (ids: string[], cb: () => void) => {
      try {
        await invitationsCreate({
          chatId: props.collab.id,
          type: LiveChatInvitationSubjectType.COLLAB,
          recipientIds: ids,
        });
        showInviteModal.value = false;
      } finally {
        cb();
      }
    };

    const updateCollabModerators = async () => {
      processing.value = true;
      try {
        await providers.CollabLiveChatsProvider.update({
          data: {
            id: props.collab.id,
            moderatorIds: moderatorIds.value,
          },
        });
      } finally {
        afterUpdateCbs.value.map(cb => cb());
        afterUpdateCbs.value = [];
        processing.value = false;
      }
    };

    const onUpdateModerator = (id: string, cb?: () => void) => {
      moderatorIds.value = moderatorIds.value.includes(id)
        ? moderatorIds.value.filter(i => i !== id)
        : [...moderatorIds.value, id];
      cb && afterUpdateCbs.value.push(cb);
    };

    const onCancel = async (id: string, cb?: () => void) => {
      try {
        await cancelInvitation(id);
      } finally {
        cb && cb();
      }
    };

    const getData = async () => {
      try {
        loading.value = true;
        await invitationsSearch({
          statuses: [LiveChatInvitationStatus.NEW, LiveChatInvitationStatus.ACCEPTED],
          subject: { subjectId: props.collab.id, subjectType: LivechatSubjectType.COLLAB_LIVE },
        });
      } finally {
        loading.value = false;
      }
    };

    const onInvitationUpdated = (data?: LiveChatInvitation) => {
      if (!data) return;
      if (data.status === LiveChatInvitationStatus.CANCELLED) {
        invitations.value = invitations.value.filter(i => i.id !== data.id);
      } else {
        invitations.value = invitations.value.map(i => (i.id === data.id ? data : i));
      }
    };

    getData();

    watch(
      moderatorIds,
      debounce(async (value, oldValue) => {
        if (isEqual(value, oldValue)) return;
        await updateCollabModerators();
      }, 500)
    );

    watch(toRef(props, "collab"), () => {
      moderatorIds.value = props.collab.moderatorIds;
    });

    emitter.on<LiveChatInvitation>(EVENTS.LIVE_INVITATION_UPDATED, onInvitationUpdated);
    onUnmounted(() => {
      emitter.off<LiveChatInvitation>(EVENTS.LIVE_INVITATION_UPDATED, onInvitationUpdated);
    });

    return {
      loading,
      broadcastingUsersNumber,
      showInviteButton,
      showInviteModal,
      pendingInvitation,
      authUserStrict,
      acceptedInvitation,
      cancelInvitation,
      onCreateInvitations,
      onCancel,
      isUserModerator,
      onUpdateModerator,
      getUserAmountByReferrer,
      userIdsToExcludeInSearch,
      processing,
    };
  },
});
